import React, { useState, useEffect, useCallback} from 'react';
import apiClient from '../utils/apiClient';
import { getAllActiveAgents, getAllAgents, deactivateAgent, activateAgent } from "../utils/APIRoutes";
import './AgentManagement.css';

const AgentManagement = () => {
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'all'

  // SECURITY: swapped `axios` for `apiClient` throughout this file.
  // getAllAgents (the "all agents" tab) is now admin-only server-side
  // — a plain axios call would 401 for it. getAllActiveAgents stays
  // public on the backend (guest checkout needs it), so the swap here
  // changes nothing for that one; it's just consistent either way.
  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = activeTab === 'active' ? getAllActiveAgents : getAllAgents;
      const response = await apiClient.get(url);
      // Handle both response formats (array directly or object with agents property)
      const agentsData = Array.isArray(response.data) ? response.data : response.data.agents || [];
      setAgents(agentsData);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch agents');
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const toggleAgentStatus = async (agentId, currentStatus) => {
  try {
    setError('');
    setSuccess('');
    
    let response;
    if (!currentStatus) {
      // Agent is inactive, so we need to activate them
      response = await apiClient.put(`${activateAgent}/${agentId}`);
    } else {
      // Agent is active, so we need to deactivate them
      response = await apiClient.put(`${deactivateAgent}/${agentId}`);
    }
    
    const { data } = response;
    
    if (data.status) {
      // Update the local state
      setAgents(agents.map(agent => 
        agent._id === agentId ? { ...agent, isActive: !currentStatus } : agent
      ));
      
      // Clear any previous success message and set new one
      setTimeout(() => setSuccess(''), 3000);
      setSuccess(`Agent ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Optional: Refetch agents to ensure data consistency
      // fetchAgents();
    } else {
      setError(data.msg || 'Update failed on server');
    }
  } catch (err) {
    setError(err.response?.data?.msg || `Failed to ${!currentStatus ? 'activate' : 'deactivate'} agent`);
    console.error('Update error:', err.response?.data);
  }
};

  const handleDeactivate = async (agentId) => {
    try {
      setError('');
      setSuccess('');
      const { data } = await apiClient.put(`${deactivateAgent}/${agentId}`);
      
      if (data.status) {
        setAgents(agents.map(agent => 
          agent._id === agentId ? { ...agent, isActive: false } : agent
        ));
        setSuccess('Agent deactivated successfully');
      } else {
        setError(data.msg || 'Deactivation failed');
      }
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to deactivate agent');
      console.error('Deactivation error:', err.response?.data);
    }
  };

  const handleActivateAgent = async (agentId) => {
  try {
    setError('');
    setSuccess('');
    const { data } = await apiClient.put(`${activateAgent}/${agentId}`);
    
    if (data.status) {
      setAgents(agents.map(agent => 
        agent._id === agentId ? { ...agent, isActive: true } : agent
      ));
      setSuccess('Agent activated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(data.msg || 'Activation failed');
    }
  } catch (err) {
    setError(err.response?.data?.msg || 'Failed to activate agent');
    console.error('Activation error:', err.response?.data);
  }
};

  const filteredAgents = agents.filter(agent =>
    agent.fullName.toLowerCase().includes(searchTerm) ||
    agent.phoneNumber.includes(searchTerm) ||
    agent.email.toLowerCase().includes(searchTerm) ||
    agent.location.toLowerCase().includes(searchTerm)
  );

  if (loading) return <div className="loading-spinner">Loading agents...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="agent-management-container">
      <div className="management-header">
        <h2>Agent Management</h2>
        {success && <div className="success-message">{success}</div>}
      </div>
      
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active Agents
        </button>
        <button
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Agents
        </button>
      </div>

      <div className="controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search agents by name, phone, email or location..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      <div className="agents-table-container">
        {filteredAgents.length === 0 ? (
          <div className="no-results">No agents found matching your search</div>
        ) : (
          <table className="agents-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map(agent => (
                <tr key={agent._id} className={!agent.isActive ? 'inactive-agent' : ''}>
                  <td className="agent-name">
                    {agent.fullName}
                    <div className="agent-email">{agent.email}</div>
                  </td>
                  <td className="agent-phone">{agent.phoneNumber}</td>
                  <td className="agent-location">{agent.location}</td>
                  <td>
                    <span className={`status-badge ${agent.isActive ? 'active' : 'inactive'}`}>
                      {agent.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions">
  {activeTab === 'all' && !agent.isActive ? (
    // Show activate button for inactive agents in 'all' tab
    <button
      onClick={() => handleActivateAgent(agent._id)}
      className="status-btn activate"
    >
      Activate Agent
    </button>
  ) : activeTab === 'active' ? (
    // Show deactivate button for active agents in 'active' tab
    <>
      <button
        onClick={() => toggleAgentStatus(agent._id, agent.isActive)}
        className="status-btn deactivate"
      >
        Deactivate
      </button>
      <button
        onClick={() => handleDeactivate(agent._id)}
        className="deactivate-btn"
      >
        Force Deactivate
      </button>
    </>
  ) : (
    // For active agents in 'all' tab
    <button
      onClick={() => toggleAgentStatus(agent._id, agent.isActive)}
      className="status-btn deactivate"
    >
      Deactivate
    </button>
  )}
</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AgentManagement;
